<template>
  <div
    v-if="showHintBox"
    class="hint"
  >
    <div class="content">
      <div :class="titleClass">
        <i
          v-if="showIcon || showIconBig"
          :class="iconClass(currentHint.type)"
        />
        <h2 :class="contentClass">
          {{ currentHint.title }}
        </h2>
        <i
          class="fas fa-times icon close"
          @click="closeHint"
        />
      </div>
      <div :class="contentClass">
        <span v-dompurify-html="currentHint.description" />
      </div>
      <div :class="contentClass">
        <div class="footer">
          <Checkbox
            v-model="currentHint.hidden"
            class="block-hint"
            @on-change="updateHintPreference"
          >
            <b>Don't show me this hint again</b>
          </Checkbox>
          <div
            v-if="visibleHints.length > 1"
            class="navigator"
          >
            <Button
              :disabled="isDisabled('prev')"
              class="button"
              @click="showHint('prev')"
            >
              <i class="fas fa-chevron-circle-left icon" />
            </Button>
            <Button
              :disabled="isDisabled('next')"
              class="button"
              @click="showHint('next')"
            >
              <i class="fas fa-chevron-circle-right icon" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    hints: Array,
    showHintBox: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: false
    },
    showIconBig: {
      type: Boolean,
      default: false
    },
    colorIcon: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      visibleHints: this.hints.filter(hint => !hint.hidden),
      currentIndex: 0
    }
  },

  computed: {
    titleClass () {
      return `title-section ${(this.showIconBig) ? 'has-icon' : ''}`
    },

    contentClass () {
      return `content-section ${(this.showIconBig) ? 'has-icon' : ''}`
    },

    currentHint () {
      return this.visibleHints[this.currentIndex]
    }
  },

  methods: {
    /**
       * Displays the corresponding icon according with the hint type.
       * @param {String} type
       * @return {String}
       */
    iconClass (type) {
      const size = this.showIconBig ? 'big' : ''
      const color = this.colorIcon ? 'color' : ''
      return type === 'info' ? `fas fa-info-circle icon ${color} ${size}`
        : (type === 'success' ? `fas fa-check-circle icon ${color} ${size}`
          : (type === 'warning' ? `fas fa-exclamation-circle icon ${color} ${size}`
            : (type === 'error' ? `fas fa-times-circle icon ${color} ${size}` : '')))
    },

    /**
       * Check if the "prev" / "next" button should be disabled according with the current hint displayed.
       * @param {String} button   It could be: "prev" / "next"
       * @return {Boolean}
       */
    isDisabled (button) {
      const lastIndex = this.visibleHints.length - 1
      return (button === 'prev' && this.currentIndex === 0) || (button === 'next' && this.currentIndex === lastIndex)
    },

    /**
       * Displays the previous or next hint according with the action.
       * @param {String} action   It could be: "prev" / "next"
       */
    showHint (action) {
      this.currentIndex += action === 'next' ? 1 : -1
    },

    /**
       * Updates the status of the current hint: Blocked / Unblocked
       */
    updateHintPreference () {
      this.$emit('updateHintPreference', this.currentHint.id, this.currentHint.hidden)
    },

    /**
       * Closes the hint window.
       */
    closeHint () {
      this.$emit('closeHint')
    }
  }

}
</script>

<style scoped>
  .hidden {
    display: none;
  }

  .hint {
    margin-top: 16px;
    margin-right: 16px;
    position: fixed;
    top: 0;
    right: 0;
    z-index: 9999;
    width: 256px;
    max-width: 288px;
  }

  .icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .icon.big {
    font-size: 32px;
    width: 32px;
    height: 32px;
    position: absolute;
    text-align: center;
  }

  .icon.close {
    margin-left: 8px;
    margin-right: 0;
    cursor: pointer;
    color: var(--color-black);
  }

  .hint,
  .block-hint >>> .ivu-checkbox-wrapper {
    font-size: 12px;
  }

  .content {
    margin: 0;
    padding: 8px;
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    box-shadow: 0 1px 6px var(--color-box-shadow);
    display: flex;
    flex-direction: column;
    background-color: var(--color-white);
  }

  .content .title-section {
    margin-bottom: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .content .title-section.has-icon {
    align-items: flex-start;
  }

  .content .title-section h2 {
    font-size: 14px;
    width: 100%;
  }

  .content .title-section h2.has-icon,
  .content .content-section.has-icon {
    margin-left: 40px;
  }

  .content .footer {
    margin-top: 8px;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  .content .footer .block-hint {
    width: 100%;
  }

  .content .footer .navigator {
    width: 48px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }

  .content .footer .navigator .button {
    margin: 0;
    padding: 0;
    width: 24px;
    line-height: 1;
    cursor: pointer;
    background-color: transparent;
    border: none;
  }

  .content .footer .navigator .button .icon {
    margin: 0;
    font-size: 18px;
    text-align: center;
    color: var(--color-green-lime);
  }

  .content .footer .navigator .button:disabled .icon {
    color: var(--color-gray-button)
  }

  .fas.fa-info-circle.color {
    color: var(--color-info);
  }

  .fas.fa-check-circle.color {
    color: var(--color-success);
  }

  .fas.fa-exclamation-circle.color {
    color: var(--color-warning);
  }

  .fas.fa-times-circle.color {
    color: var(--color-red-border);
  }

  @media screen and (min-width: 1024px) {
    .hint {
      min-width: 336px;
      max-width: 400px;
    }
    .icon.close {
      color: var(--color-gray-button);
    }

    .icon.close:hover {
      color: var(--color-black);
    }
  }
</style>
